(() => {
  const enhanceSlider = (root) => {
    if (!root || root.dataset.ready === 'true') return;
    root.dataset.ready = 'true';
    const slides = Array.from(root.querySelectorAll('[data-amour-home-slide]'));
    const dots = root.querySelector('[data-amour-home-dots]');
    const pause = root.querySelector('[data-amour-home-pause]');
    if (!slides.length || !dots) return;
    let index = 0;
    let paused = root.dataset.amourBfHold === 'true' || window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    let timer = null;
    let pointerStart = null;

    const show = (next, userInitiated = false) => {
      index = (next + slides.length) % slides.length;
      slides.forEach((slide, slideIndex) => {
        const active = slideIndex === index;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', String(!active));
        slide.querySelectorAll('a, button, input, select, textarea').forEach((control) => {
          if (active) control.removeAttribute('tabindex'); else control.setAttribute('tabindex', '-1');
        });
      });
      dots.querySelectorAll('button').forEach((dot, dotIndex) => {
        dot.classList.toggle('is-active', dotIndex === index);
        dot.setAttribute('aria-current', dotIndex === index ? 'true' : 'false');
      });
      if (userInitiated) restart();
    };
    const stop = () => { if (timer) window.clearInterval(timer); timer = null; };
    const start = () => {
      stop();
      if (!paused && slides.length > 1) timer = window.setInterval(() => show(index + 1), 7000);
    };
    const restart = () => { stop(); start(); };

    slides.forEach((_, slideIndex) => {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = `amour-home-hero__dot${slideIndex === 0 ? ' is-active' : ''}`;
      dot.setAttribute('aria-label', `${root.getAttribute('aria-label') || 'Hero'} ${slideIndex + 1}`);
      dot.innerHTML = '<span aria-hidden="true"></span>';
      dot.addEventListener('click', () => show(slideIndex, true));
      dots.append(dot);
    });
    if (slides.length < 2) root.querySelector('[data-amour-home-controls]')?.setAttribute('hidden', '');
    pause?.addEventListener('click', () => {
      paused = !paused;
      pause.setAttribute('aria-pressed', String(paused));
      pause.textContent = paused ? pause.dataset.playLabel : pause.dataset.pauseLabel;
      if (paused) stop(); else start();
    });
    root.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowRight') show(index + 1, true);
      if (event.key === 'ArrowLeft') show(index - 1, true);
    });
    root.addEventListener('pointerdown', (event) => { pointerStart = { x: event.clientX, y: event.clientY }; });
    root.addEventListener('pointerup', (event) => {
      if (!pointerStart) return;
      const dx = event.clientX - pointerStart.x;
      const dy = event.clientY - pointerStart.y;
      pointerStart = null;
      if (Math.abs(dx) > 48 && Math.abs(dx) > Math.abs(dy)) show(index + (dx < 0 ? 1 : -1), true);
    });
    root.addEventListener('mouseenter', stop);
    root.addEventListener('mouseleave', start);
    root.addEventListener('focusin', stop);
    root.addEventListener('focusout', (event) => { if (!root.contains(event.relatedTarget)) start(); });
    if (paused && pause) {
      pause.setAttribute('aria-pressed', 'true');
      pause.textContent = pause.dataset.playLabel;
    }
    show(0);
    start();
  };

  const enhanceVideo = (root) => {
    if (!root || root.dataset.videoReady === 'true') return;
    root.dataset.videoReady = 'true';
    const video = root.querySelector('video');
    const button = root.querySelector('[data-amour-home-video-play]');
    if (!video || !button) return;
    button.addEventListener('click', async () => {
      root.classList.add('is-playing');
      video.controls = true;
      try { await video.play(); } catch (_) { root.classList.remove('is-playing'); }
    });
    video.addEventListener('ended', () => {
      root.classList.remove('is-playing');
      video.controls = false;
    });
  };

  const enhanceRingFinder = (root) => {
    if (!root || root.dataset.ringFinderReady === 'true') return;
    root.dataset.ringFinderReady = 'true';
    const toggle = root.querySelector('[data-amour-ring-finder-toggle]');
    const panel = root.querySelector('[data-amour-ring-finder-panel]');
    const choices = Array.from(root.querySelectorAll('[data-amour-ring-finder-choice]'));
    const recommendations = Array.from(root.querySelectorAll('[data-amour-ring-finder-recommendation]'));
    if (!toggle || !panel || !choices.length || !recommendations.length) return;

    const setOpen = (open, returnFocus = false) => {
      root.classList.toggle('is-open', open);
      panel.hidden = !open;
      toggle.setAttribute('aria-expanded', String(open));
      toggle.setAttribute('aria-label', open ? toggle.dataset.closeLabel : toggle.dataset.openLabel);
      if (!open && returnFocus) toggle.focus();
    };

    const activate = (choice, moveFocus = false) => {
      const key = choice.dataset.amourRingFinderChoice;
      choices.forEach((item) => {
        const selected = item === choice;
        item.classList.toggle('is-selected', selected);
        item.setAttribute('aria-pressed', String(selected));
      });
      recommendations.forEach((recommendation) => {
        const selected = recommendation.dataset.amourRingFinderRecommendation === key;
        recommendation.classList.toggle('is-active', selected);
        recommendation.hidden = !selected;
      });
      if (moveFocus) choice.focus();
    };

    toggle.addEventListener('click', () => setOpen(toggle.getAttribute('aria-expanded') !== 'true'));
    root.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape' || toggle.getAttribute('aria-expanded') !== 'true') return;
      event.preventDefault();
      setOpen(false, true);
    });

    choices.forEach((choice, index) => {
      choice.addEventListener('click', () => activate(choice));
      choice.addEventListener('keydown', (event) => {
        if (!['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        let next = index;
        if (event.key === 'ArrowLeft') next = (index - 1 + choices.length) % choices.length;
        if (event.key === 'ArrowRight') next = (index + 1) % choices.length;
        if (event.key === 'ArrowUp') next = (index - 2 + choices.length) % choices.length;
        if (event.key === 'ArrowDown') next = (index + 2) % choices.length;
        if (event.key === 'Home') next = 0;
        if (event.key === 'End') next = choices.length - 1;
        activate(choices[next], true);
      });
    });

    setOpen(false);
  };

  const enhanceRail = (rail) => {
    if (!rail || rail.dataset.railReady === 'true') return;
    rail.dataset.railReady = 'true';
    const isJournal = rail.classList.contains('amour-home-journal__rail');
    const journalCards = isJournal ? Array.from(rail.querySelectorAll('.amour-home-journal__card')) : [];
    const journalSection = isJournal ? rail.closest('.amour-home-journal') : null;
    const journalDots = journalSection ? Array.from(journalSection.querySelectorAll('[data-amour-home-journal-dots] span')) : [];
    const journalPrev = journalSection ? journalSection.querySelector('[data-amour-home-journal-prev]') : null;
    const journalNext = journalSection ? journalSection.querySelector('[data-amour-home-journal-next]') : null;
    const mobile = window.matchMedia('(max-width: 767px)');
    const desktop = window.matchMedia('(min-width: 1024px)');
    let startX = 0;
    let startScroll = 0;
    let dragging = false;
    let journalFrame = null;

    const updateJournalDots = () => {
      if (!isJournal || !journalCards.length || !mobile.matches) return;
      const railCenter = rail.scrollLeft + (rail.clientWidth / 2);
      let closestIndex = 0;
      let closestDistance = Infinity;
      journalCards.forEach((card, index) => {
        const distance = Math.abs((card.offsetLeft + (card.offsetWidth / 2)) - railCenter);
        if (distance < closestDistance) { closestDistance = distance; closestIndex = index; }
      });
      journalDots.forEach((dot, index) => dot.classList.toggle('is-active', index === closestIndex));
    };

    const centerInitialJournalCard = () => {
      if (!isJournal || !mobile.matches || journalCards.length < 2) return;
      const card = journalCards[1];
      const target = card.offsetLeft - ((rail.clientWidth - card.offsetWidth) / 2);
      rail.scrollTo({ left: target, behavior: 'auto' });
      journalDots.forEach((dot, index) => dot.classList.toggle('is-active', index === 1));
    };

    const updateJournalControls = () => {
      if (!isJournal || !journalPrev || !journalNext || !desktop.matches) return;
      const maxScroll = Math.max(0, rail.scrollWidth - rail.clientWidth);
      journalPrev.disabled = rail.scrollLeft <= 2;
      journalNext.disabled = rail.scrollLeft >= maxScroll - 2;
    };

    const moveJournalPage = (direction) => {
      if (!isJournal || !desktop.matches) return;
      const maxScroll = Math.max(0, rail.scrollWidth - rail.clientWidth);
      rail.scrollTo({ left: direction > 0 ? maxScroll : 0, behavior: 'smooth' });
    };
    rail.addEventListener('pointerdown', (event) => {
      if (event.pointerType !== 'mouse') return;
      startX = event.clientX;
      startScroll = rail.scrollLeft;
      dragging = true;
      rail.setPointerCapture(event.pointerId);
    });
    rail.addEventListener('pointermove', (event) => {
      if (!dragging) return;
      rail.scrollLeft = startScroll - (event.clientX - startX);
    });
    rail.addEventListener('pointerup', () => { dragging = false; });
    rail.addEventListener('pointercancel', () => { dragging = false; });
    if (isJournal) {
      rail.addEventListener('scroll', () => {
        if (journalFrame) window.cancelAnimationFrame(journalFrame);
        journalFrame = window.requestAnimationFrame(() => {
          updateJournalDots();
          updateJournalControls();
        });
      }, { passive: true });
      journalPrev?.addEventListener('click', () => moveJournalPage(-1));
      journalNext?.addEventListener('click', () => moveJournalPage(1));
      rail.addEventListener('keydown', (event) => {
        if (!desktop.matches || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        moveJournalPage(event.key === 'ArrowRight' || event.key === 'End' ? 1 : -1);
      });
      window.addEventListener('resize', updateJournalControls, { passive: true });
      window.requestAnimationFrame(centerInitialJournalCard);
      window.requestAnimationFrame(updateJournalControls);
    }
  };

  const enhanceEclatRail = (rail) => {
    if (!rail || rail.dataset.eclatReady === 'true') return;
    rail.dataset.eclatReady = 'true';
    const originals = Array.from(rail.querySelectorAll('[data-amour-home-eclat-item]'));
    const dots = Array.from(rail.parentElement.querySelectorAll('[data-amour-home-eclat-dot]'));
    const captionTitle = rail.parentElement.querySelector('[data-amour-home-eclat-title]');
    const captionCopy = rail.parentElement.querySelector('[data-amour-home-eclat-copy]');
    const mobile = window.matchMedia('(max-width: 767px)');
    let frame = null;
    let settleTimer = null;
    let isRebasing = false;
    let items = originals;

    if (mobile.matches && originals.length > 1) {
      const makeClone = (item) => {
        const clone = item.cloneNode(true);
        clone.classList.remove('is-featured');
        clone.dataset.eclatClone = 'true';
        clone.setAttribute('aria-hidden', 'true');
        clone.querySelectorAll('a').forEach((link) => link.setAttribute('tabindex', '-1'));
        return clone;
      };
      const before = originals.map(makeClone);
      const after = originals.map(makeClone);
      rail.prepend(...before);
      rail.append(...after);
      items = Array.from(rail.querySelectorAll('[data-amour-home-eclat-item]'));
    }

    const itemTarget = (item) => item.offsetLeft - ((rail.clientWidth - item.offsetWidth) / 2);

    const updateCaption = (logicalIndex) => {
      const source = originals[logicalIndex];
      if (!source) return;
      if (captionTitle) captionTitle.textContent = source.dataset.eclatTitle || '';
      if (captionCopy) captionCopy.textContent = source.dataset.eclatCopy || '';
    };

    const setActive = (physicalIndex, shouldScroll = false) => {
      const item = items[physicalIndex];
      if (!item) return;
      const logicalIndex = Number(item.dataset.eclatIndex);
      items.forEach((candidate, itemIndex) => candidate.classList.toggle('is-featured', itemIndex === physicalIndex));
      dots.forEach((dot, dotIndex) => {
        const active = dotIndex === logicalIndex;
        dot.classList.toggle('is-active', active);
        if (active) dot.setAttribute('aria-current', 'true'); else dot.removeAttribute('aria-current');
      });
      updateCaption(logicalIndex);
      if (shouldScroll && mobile.matches) rail.scrollTo({ left: itemTarget(item), behavior: 'smooth' });
    };

    const closestPhysicalIndex = () => {
      const railCenter = rail.scrollLeft + (rail.clientWidth / 2);
      let closestIndex = mobile.matches && items.length > originals.length ? originals.length + 1 : 1;
      let closestDistance = Infinity;
      items.forEach((item, index) => {
        const distance = Math.abs((item.offsetLeft + (item.offsetWidth / 2)) - railCenter);
        if (distance < closestDistance) { closestDistance = distance; closestIndex = index; }
      });
      return closestIndex;
    };

    const rebaseTo = (physicalIndex) => {
      const item = items[physicalIndex];
      if (!item) return;
      isRebasing = true;
      rail.classList.add('is-rebasing');
      const previousBehavior = rail.style.scrollBehavior;
      const previousSnap = rail.style.scrollSnapType;
      rail.style.scrollBehavior = 'auto';
      rail.style.scrollSnapType = 'none';
      rail.scrollLeft = itemTarget(item);
      setActive(physicalIndex);
      rail.getBoundingClientRect();
      window.requestAnimationFrame(() => {
        rail.style.scrollBehavior = previousBehavior;
        rail.style.scrollSnapType = previousSnap;
        rail.classList.remove('is-rebasing');
        isRebasing = false;
      });
    };

    const normalizeInfinitePosition = () => {
      if (!mobile.matches || items.length === originals.length) return;
      const current = closestPhysicalIndex();
      let equivalent = current;
      if (current < originals.length) equivalent = current + originals.length;
      if (current >= originals.length * 2) equivalent = current - originals.length;
      if (equivalent !== current) rebaseTo(equivalent);
    };

    const updateFromScroll = () => {
      if (!mobile.matches || isRebasing) return;
      setActive(closestPhysicalIndex());
      window.clearTimeout(settleTimer);
      settleTimer = window.setTimeout(normalizeInfinitePosition, 170);
    };

    const centerNecklaces = () => {
      if (!mobile.matches || originals.length < 2) return;
      const physicalIndex = items.length === originals.length ? 1 : originals.length + 1;
      rebaseTo(physicalIndex);
    };

    rail.addEventListener('scroll', () => {
      if (frame) window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(updateFromScroll);
    }, { passive: true });
    dots.forEach((dot) => dot.addEventListener('click', () => {
      const logicalIndex = Number(dot.dataset.amourHomeEclatDot);
      const physicalIndex = items.length === originals.length ? logicalIndex : originals.length + logicalIndex;
      setActive(physicalIndex, true);
    }));
    rail.addEventListener('keydown', (event) => {
      if (!mobile.matches || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
      event.preventDefault();
      let physicalIndex = closestPhysicalIndex();
      if (event.key === 'ArrowLeft') physicalIndex -= 1;
      if (event.key === 'ArrowRight') physicalIndex += 1;
      if (event.key === 'Home') physicalIndex = items.length === originals.length ? 0 : originals.length;
      if (event.key === 'End') physicalIndex = items.length === originals.length ? originals.length - 1 : (originals.length * 2) - 1;
      setActive(Math.max(0, Math.min(items.length - 1, physicalIndex)), true);
    });
    window.requestAnimationFrame(centerNecklaces);
  };

  const init = () => {
    document.querySelectorAll('[data-amour-home-slider]').forEach(enhanceSlider);
    document.querySelectorAll('[data-amour-ring-finder]').forEach(enhanceRingFinder);
    document.querySelectorAll('[data-amour-home-video]').forEach(enhanceVideo);
    document.querySelectorAll('[data-amour-home-rail]').forEach(enhanceRail);
    document.querySelectorAll('[data-amour-home-eclat-rail]').forEach(enhanceEclatRail);
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true }); else init();
  document.addEventListener('shopify:section:load', init);
})();
